import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Button } from '@uhg-abyss/web/ui/Button';
import { ScreenQueries, styled } from '@uhg-abyss/web/tools/styled';
import { AccountInfoBox } from './AccountInfoBox';
import { useAccounts } from '@/context/AccountContext';

const AccountList = styled('ul', {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
});

// Radius + clipping live on the li (real first/last-child) since Emotion's
// component-selector interpolation doesn't work through this styled() wrapper.
const AccountListItem = styled('li', {
    cursor: 'pointer',
    overflow: 'hidden',
    '&:first-of-type': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
    '&:last-of-type': { borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
});

const DrawerAccountInfoBox = styled(AccountInfoBox, {
    borderRadius: '0px',
    '&:hover': {
        borderColor: '$button.color.surface.container.filled-brand.rest',
        borderLeftWidth: '0.25rem',
    },
    variants: {
        selected: {
            true: {
                borderColor: '$button.color.surface.container.filled-brand.rest',
                borderLeftWidth: '0.25rem',
            },
        },
    },
});

const DrawerFooter = styled('div', { paddingTop: '8px' });

const FullWidthButton = styled(Button, { width: '100%' });

// DialogContainer (the flex row that centers/end-justifies the drawer panel) has no
// exposed part name, so its alignItems/justifyContent can't be targeted directly -
// fully re-anchoring content-container doesn't fix it completely either and left as-is it
// pushes a now-full-width panel (and its close button) past the right edge of the viewport.
// Per conversation with Tracy we will accept this visual glitch as you can click off the drawer
// to close it, and we will look to revisit this in a future mobile ui focused ticket.
const mobileBottomSheetOverride = {
    'abyss-modal-base-content-container': {
        [ScreenQueries.SmallerThanMedium]: {
            marginLeft: 0,
            maxWidth: '100%',
            height: 'auto',
            maxHeight: '85vh',
        },
    },
};

// Manages its own pending selection; closing without confirming leaves the active account unchanged.
export const AccountSwitcherDrawer = () => {
    const { t } = useTranslation();

    const {
        isAccountSwitcherOpen: isOpen,
        accounts,
        selectedAccountId,
        setSelectedAccountByResourceId,
        setIsAccountSwitcherOpen,
    } = useAccounts();

    const [pendingSelectedAccountId, setPendingSelectedAccountId] = useState<number>(selectedAccountId);

    useEffect(() => {
        if (isOpen) {
            setPendingSelectedAccountId(selectedAccountId);
        }
    }, [isOpen, selectedAccountId, setPendingSelectedAccountId]);

    const handleSelectClick = useCallback(() => {
        setSelectedAccountByResourceId(pendingSelectedAccountId);
        setIsAccountSwitcherOpen(false);
    }, [setSelectedAccountByResourceId, pendingSelectedAccountId, setIsAccountSwitcherOpen]);

    const onClose = useCallback(() => {
        setIsAccountSwitcherOpen(false);
    }, [setIsAccountSwitcherOpen]);

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            position="right"
            header={t('selectAnAccount')}
            css={mobileBottomSheetOverride}
            footer={
                <DrawerFooter>
                    <FullWidthButton id="selectAccountBtn" onClick={handleSelectClick}>{t('selectAccountButton')}</FullWidthButton>
                </DrawerFooter>
            }
        >
            <AccountList>
                {accounts.map((account) => (
                    <AccountListItem key={account.resourceId} onClick={() => setPendingSelectedAccountId(account.resourceId)}>
                        <DrawerAccountInfoBox
                            userId={account.userId}
                            domain={account.resourceName}
                            isLocked={account.isLocked}
                            selected={pendingSelectedAccountId === account.resourceId}
                            hideLockStatus={true}
                        />
                    </AccountListItem>
                ))}
            </AccountList>
        </Drawer>
    );
};
